// src/components/wholesaler/Checkout.js
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, Lock, Truck, CreditCard, MapPin } from 'lucide-react';

// Initialize Stripe - this key should come from your Firebase config/environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

export function Checkout({ 
  cart, 
  userData, 
  subtotal, 
  ccFee, 
  totalWithFee, 
  onSuccess, 
  onCancel 
}) {
  const [shippingAddress, setShippingAddress] = useState({
    street: userData?.shippingAddress?.street || '',
    city: userData?.shippingAddress?.city || '',
    state: userData?.shippingAddress?.state || '',
    zip: userData?.shippingAddress?.zip || ''
  });
  
  const [dropshipAddress, setDropshipAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    name: '',
    company: ''
  });
  
  const [useDropship, setUseDropship] = useState(false);
  const [notes, setNotes] = useState('');

  // When toggling off dropship, clear the dropship fields
  useEffect(() => {
    if (!useDropship) {
      setDropshipAddress({
        street: '',
        city: '',
        state: '',
        zip: '',
        name: '',
        company: ''
      });
    }
  }, [useDropship]);

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        cart={cart}
        userData={userData}
        subtotal={subtotal}
        ccFee={ccFee}
        totalWithFee={totalWithFee}
        shippingAddress={shippingAddress}
        setShippingAddress={setShippingAddress}
        dropshipAddress={dropshipAddress}
        setDropshipAddress={setDropshipAddress}
        useDropship={useDropship}
        setUseDropship={setUseDropship}
        notes={notes}
        setNotes={setNotes}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}

function CheckoutForm({
  cart,
  userData,
  subtotal,
  ccFee,
  totalWithFee,
  shippingAddress,
  setShippingAddress,
  dropshipAddress,
  setDropshipAddress,
  useDropship,
  setUseDropship,
  notes,
  setNotes,
  onSuccess,
  onCancel
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    // Validate shipping address
    const finalShippingAddress = useDropship ? dropshipAddress : shippingAddress;
    if (!finalShippingAddress.street || !finalShippingAddress.city || 
        !finalShippingAddress.state || !finalShippingAddress.zip) {
      setError('Please fill in all shipping address fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent via Cloud Function
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
      const { data } = await createPaymentIntent({
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          wholesalePrice: item.wholesalePrice
        })),
        shippingAddress: finalShippingAddress,
        isDropship: useDropship,
        notes: notes
      });

      const { clientSecret, orderId } = data;

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: userData?.contactName || userData?.companyName,
              email: userData?.email
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(orderId);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'An error occurred during checkout');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Checkout</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Addresses */}
          <div className="space-y-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Truck size={20} />
                <h3 className="text-lg font-semibold">Shipping Address</h3>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Street Address"
                  value={shippingAddress.street}
                  onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required={!useDropship}
                />
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="City"
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required={!useDropship}
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={shippingAddress.state}
                    onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    maxLength={2}
                    required={!useDropship}
                  />
                  <input
                    type="text"
                    placeholder="ZIP"
                    value={shippingAddress.zip}
                    onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required={!useDropship}
                  />
                </div>
              </div>
            </div>

            {/* Drop Ship Option */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useDropship}
                  onChange={(e) => setUseDropship(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <div className="flex items-center gap-2">
                  <MapPin size={20} />
                  <span className="font-semibold">Ship to a different address (Drop Ship)</span>
                </div>
              </label>

              {useDropship && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <input
                    type="text"
                    placeholder="Recipient Name"
                    value={dropshipAddress.name}
                    onChange={(e) => setDropshipAddress({...dropshipAddress, name: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Company Name (optional)"
                    value={dropshipAddress.company}
                    onChange={(e) => setDropshipAddress({...dropshipAddress, company: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={dropshipAddress.street}
                    onChange={(e) => setDropshipAddress({...dropshipAddress, street: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="City"
                      value={dropshipAddress.city}
                      onChange={(e) => setDropshipAddress({...dropshipAddress, city: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={dropshipAddress.state}
                      onChange={(e) => setDropshipAddress({...dropshipAddress, state: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      maxLength={2}
                      required
                    />
                    <input
                      type="text"
                      placeholder="ZIP"
                      value={dropshipAddress.zip}
                      onChange={(e) => setDropshipAddress({...dropshipAddress, zip: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Order Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Order Notes (Optional)</h3>
              <textarea
                placeholder="Any special instructions for your order..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg resize-none"
              />
            </div>
          </div>

          {/* Right Column - Payment & Summary */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{item.name} × {item.quantity}</span>
                    <span>${(item.wholesalePrice * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t mt-4 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CC Processing Fee</span>
                  <span>${ccFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>${totalWithFee.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard size={20} />
                <h3 className="text-lg font-semibold">Payment</h3>
              </div>

              <div className="border rounded-lg p-4 mb-4">
                <CardElement options={cardElementOptions} />
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Lock size={14} />
                <span>Your payment information is encrypted and secure</span>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={!stripe || loading}
                  className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock size={18} />
                      Pay ${totalWithFee.toFixed(2)}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onCancel}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200"
                >
                  Back to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
